import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookings, calendar, tasks } from '../../lib/api';
import { AlertCircle, Calendar, Check } from 'lucide-react';
import BookingCreateForm from './bookings/BookingCreateForm';
import BookingEmptyState from './bookings/BookingEmptyState';
import BookingList from './bookings/BookingList';
import CalendarConnectionCard from './bookings/CalendarConnectionCard';
import EventList from './bookings/EventList';
import { defaultBookingForm, toDatetimeLocal } from './bookings/dateUtils';

export default function Bookings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState(null);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [error, setError] = useState('');
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [rowBusy, setRowBusy] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editForm, setEditForm] = useState(null);
  const [form, setForm] = useState(defaultBookingForm);
  const [taskContext, setTaskContext] = useState(null);

  const loadAll = async () => {
    setError('');
    const [bookingData, statusData] = await Promise.all([
      bookings.list(),
      calendar.status().catch((err) => ({ configured: false, connected: false, error: err.message })),
    ]);
    setItems(bookingData.items || []);
    setStatus(statusData);

    if (statusData.connected) {
      const calendarData = await calendar.calendars().catch((err) => {
        setError(err.message || 'Could not load Google calendars.');
        return { items: [], selected_calendar_id: statusData.calendar_id };
      });
      setGoogleCalendars(calendarData.items || []);
      setSelectedCalendarId(calendarData.selected_calendar_id || statusData.calendar_id || '');
      const from = new Date(Date.now() - 7 * 86400000).toISOString();
      const to = new Date(Date.now() + 30 * 86400000).toISOString();
      const eventData = await calendar.events({ from, to }).catch((err) => {
        setError(err.message || 'Could not load Google Calendar events.');
        return { items: [] };
      });
      setEvents(eventData.items || []);
    } else {
      setEvents([]);
      setGoogleCalendars([]);
      setSelectedCalendarId('');
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadAll();
      } catch (err) {
        if (active) {
          setError(err.message || 'Could not load calendar bookings.');
          setItems([]);
        }
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const connected = searchParams.get('calendar_connected');
    const calendarError = searchParams.get('calendar_error');
    if (!connected && !calendarError) return;
    if (connected === 'google') {
      setBanner({ type: 'success', text: 'Google Calendar connected successfully.' });
    } else if (calendarError) {
      setBanner({ type: 'error', text: `Google Calendar connection failed: ${calendarError}` });
    }
    navigate(`/app/calendar${searchParams.get('task_id') ? `?task_id=${encodeURIComponent(searchParams.get('task_id'))}` : ''}`, { replace: true });
  }, [navigate, searchParams]);

  useEffect(() => {
    const taskId = searchParams.get('task_id');
    if (!taskId) {
      setTaskContext(null);
      return;
    }
    let active = true;
    tasks.get(taskId)
      .then((task) => {
        if (!active) return;
        setTaskContext(task);
        if (task?.kind === 'meeting') {
          setForm((prev) => ({
            ...prev,
            title: task?.suggested_payload?.title || prev.title,
            description: task?.suggested_reply || task?.description || prev.description,
          }));
        }
      })
      .catch(() => {
        if (active) setTaskContext(null);
      });
    return () => {
      active = false;
    };
  }, [searchParams]);

  const activeItems = useMemo(() => (items || []).filter((item) => item.status !== 'cancelled'), [items]);
  const cancelled = useMemo(() => (items || []).filter((item) => item.status === 'cancelled'), [items]);
  const upcoming = useMemo(() => activeItems.filter((item) => new Date(item.start_at) >= new Date()), [activeItems]);
  const past = useMemo(() => activeItems.filter((item) => new Date(item.start_at) < new Date()), [activeItems]);

  const handleConnect = async () => {
    try {
      setBusy(true);
      const returnTo = `/app/calendar${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      const { link } = await calendar.connectLink(returnTo);
      if (!link) throw new Error('No Google connect link returned');
      window.location.href = link;
    } catch (err) {
      setError(err.message || 'Could not start Google Calendar connection.');
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    try {
      setBusy(true);
      await calendar.disconnectGoogle();
      await loadAll();
    } catch (err) {
      setError(err.message || 'Could not disconnect Google Calendar.');
    } finally {
      setBusy(false);
    }
  };

  const handleCalendarSelect = async (calendarId) => {
    try {
      setSavingCalendar(true);
      setError('');
      setSelectedCalendarId(calendarId);
      const result = await calendar.selectCalendar(calendarId);
      setBanner({ type: 'success', text: `Booking calendar changed to ${result.calendar_summary || calendarId}.` });
      await loadAll();
    } catch (err) {
      setError(err.message || 'Could not change Google Calendar.');
    } finally {
      setSavingCalendar(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');
      setBanner(null);
      const attendeeList = form.attendees
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const result = await bookings.create({
        title: form.title,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        attendees: attendeeList,
        description: form.description,
        task_id: taskContext?.id || undefined,
        conversation_id: taskContext?.conversation_id || undefined,
        lead_id: taskContext?.lead_id || undefined,
      });
      setForm(defaultBookingForm());
      if (result?.task_completed) {
        setTaskContext((prev) => (prev ? { ...prev, status: 'done' } : prev));
      }
      const meetReady = result?.booking?.meet_link ? ' Meet link is ready.' : '';
      const taskText = result?.task_completed ? ' Task was marked done.' : '';
      setBanner({ type: 'success', text: `Booking created successfully.${meetReady}${taskText}` });
      await loadAll();
    } catch (err) {
      setError(err.message || 'Could not create booking.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      title: item.title || 'Linirio meeting',
      attendees: (item.attendees || []).join(', '),
      start_at: toDatetimeLocal(item.start_at),
      end_at: toDatetimeLocal(item.end_at),
      description: item.description || '',
    });
  };

  const cancelEdit = () => {
    setEditingId('');
    setEditForm(null);
  };

  const handleSaveEdit = async (bookingId) => {
    if (!editForm) return;
    try {
      setRowBusy(bookingId);
      setError('');
      setBanner(null);
      const attendeeList = editForm.attendees
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      await bookings.update(bookingId, {
        title: editForm.title,
        start_at: new Date(editForm.start_at).toISOString(),
        end_at: new Date(editForm.end_at).toISOString(),
        attendees: attendeeList,
        description: editForm.description,
      });
      setBanner({ type: 'success', text: 'Booking updated and Google Calendar event rescheduled.' });
      cancelEdit();
      await loadAll();
    } catch (err) {
      setError(err.message || 'Could not reschedule booking.');
    } finally {
      setRowBusy('');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking and remove the Google Calendar event?')) return;
    try {
      setRowBusy(bookingId);
      setError('');
      setBanner(null);
      await bookings.cancel(bookingId);
      setBanner({ type: 'success', text: 'Booking cancelled.' });
      await loadAll();
    } catch (err) {
      setError(err.message || 'Could not cancel booking.');
    } finally {
      setRowBusy('');
    }
  };

  return (
    <div className="app-page-shell bg-white" data-testid="bookings-page">
      <header className="h-14 border-b border-[#E3E2E0] flex items-center justify-between px-6 sticky top-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#6B6B6B]" />
          <h1 className="text-lg font-semibold text-[#191919]">Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          {items && (
            <span className="hidden sm:inline text-xs text-[#6B6B6B]">
              {upcoming.length} upcoming / {past.length} past
            </span>
          )}
        </div>
      </header>

      <div className="app-page-scroll">
        <div className="max-w-6xl mx-auto p-6 space-y-5">
        {taskContext && (
          <div className="rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] px-4 py-3 text-sm text-[#191919]">
            Creating a meeting from task <span className="font-medium">{taskContext.title}</span>.
            {taskContext.status === 'done' ? ' This task is already marked done.' : ''}
          </div>
        )}
        {banner && (
          <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${banner.type === 'error' ? 'border border-[#FF8080] bg-[#FFEBE9] text-[#CE3636]' : 'border border-[#A8E89C] bg-[#EEFBDD] text-[#376B2B]'}`}>
            {banner.type === 'error' ? <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <Check className="w-4 h-4 mt-0.5 shrink-0" />}
            {banner.text}
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-[#FF8080] bg-[#FFEBE9] px-3 py-2 text-sm text-[#CE3636]">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <CalendarConnectionCard
          status={status}
          googleCalendars={googleCalendars}
          selectedCalendarId={selectedCalendarId}
          savingCalendar={savingCalendar}
          busy={busy}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          onCalendarSelect={handleCalendarSelect}
        />

        <BookingCreateForm
          form={form}
          setForm={setForm}
          creating={creating}
          connected={status?.connected}
          configured={status?.configured}
          onCreate={handleCreate}
        />

        <BookingEmptyState items={items} connected={status?.connected} />

        {items && items.length > 0 && (
          <>
            <BookingList
              title="Upcoming bookings"
              items={upcoming}
              editingId={editingId}
              editForm={editForm}
              rowBusy={rowBusy}
              onStartEdit={startEdit}
              onEditFormChange={setEditForm}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={cancelEdit}
              onCancelBooking={handleCancelBooking}
            />
            <BookingList
              title="Past bookings"
              items={past}
              muted
              editingId={editingId}
              editForm={editForm}
              rowBusy={rowBusy}
              onStartEdit={startEdit}
              onEditFormChange={setEditForm}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={cancelEdit}
              onCancelBooking={handleCancelBooking}
            />
            <BookingList title="Cancelled bookings" items={cancelled} muted cancelled />
          </>
        )}

        <EventList connected={status?.connected} events={events} />
        </div>
      </div>
    </div>
  );
}
